import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ExternalLink, Trash2, KeyRound, UserPlus, Eye, FileText, AlertCircle, Hotel as HotelIcon } from "lucide-react";

const REQUIRED_DOCS = [
  { key: "gst_url", label: "GST certificate", numberKey: "gst" },
  { key: "pan_url", label: "PAN card", numberKey: "pan" },
  { key: "bank_proof_url", label: "Bank proof / cancelled cheque", numberKey: "bank_details" },
  { key: "trade_license_url", label: "Hotel trade license", numberKey: null },
  { key: "owner_id_proof_url", label: "Owner ID (Aadhaar/PAN)", numberKey: null },
];

export default function AdminPanel() {
  const [stats, setStats] = useState(null);
  const [waitlist, setWaitlist] = useState({});
  const [hotels, setHotels] = useState([]);
  const [users, setUsers] = useState([]);
  const [leads, setLeads] = useState({ negotiations: [], booking_requests: [] });
  const [pendingPartners, setPendingPartners] = useState([]);

  // user detail modal
  const [userDetail, setUserDetail] = useState(null);
  const [resetPwOpen, setResetPwOpen] = useState(false);
  const [newPw, setNewPw] = useState("");

  // hotel detail modal
  const [hotelDetail, setHotelDetail] = useState(null);

  // create user dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", password: "", name: "", role: "hotel_partner", phone: "" });

  const load = async () => {
    const [s, h, u, l, pp, wl] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/hotels"),
      api.get("/admin/users"),
      api.get("/admin/leads"),
      api.get("/admin/partners/pending").catch(() => ({ data: { pending: [] } })),
      api.get("/waitlist/counts").catch(() => ({ data: { counts: {} } })),
    ]);
    setStats(s.data); setHotels(h.data); setUsers(u.data); setLeads(l.data);
    setPendingPartners(pp.data?.pending || []);
    setWaitlist(wl.data?.counts || {});
  };
  useEffect(() => { load(); }, []);

  const [verifiedPartners, setVerifiedPartners] = useState({});  // Iter42: {user_id: bool}
  const approvePartner = async (uid) => {
    if (!verifiedPartners[uid]) {
      toast.error("Please verify all documents first — tick the checkbox above the Approve button.");
      return;
    }
    try { await api.post(`/admin/partners/${uid}/approve`, { docs_verified: true }); toast.success("Partner approved"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const rejectPartner = async (uid) => {
    const reason = window.prompt("Reason for rejection (shown to the partner):", "Please provide additional documents.");
    if (!reason) return;
    try { await api.post(`/admin/partners/${uid}/reject`, { reason }); toast.success("Feedback sent to partner"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const approve = async (id) => { await api.post(`/admin/hotels/${id}/approve`); toast.success("Approved"); load(); setHotelDetail(null); };
  const reject = async (id) => { await api.post(`/admin/hotels/${id}/reject`); toast.success("Rejected"); load(); setHotelDetail(null); };
  const setPlan = async (id, plan) => { await api.post(`/admin/hotels/${id}/plan`, { plan }); toast.success(`Plan: ${plan}`); load(); };
  const delHotel = async (id) => {
    if (!window.confirm("Delete this hotel and all its rooms?")) return;
    await api.delete(`/admin/hotels/${id}`); toast.success("Hotel deleted"); load(); setHotelDetail(null);
  };

  const openUser = async (uid) => {
    try { const { data } = await api.get(`/admin/users/${uid}`); setUserDetail(data); }
    catch (e) { toast.error("Failed to load user"); }
  };
  const delUser = async (uid) => {
    if (!window.confirm("Delete this user? Their hotels & rooms will also be removed.")) return;
    try { await api.delete(`/admin/users/${uid}`); toast.success("User deleted"); setUserDetail(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const purgeTestAccounts = async () => {
    // Two-step: preview (dry run) → confirm → real purge
    try {
      const { data: preview } = await api.post("/admin/users/purge-test-accounts", { confirm: false });
      const n = preview.match_count;
      const skipped = preview.skipped_with_real_data?.length || 0;
      if (n === 0) {
        toast.info(`No test accounts found. ${skipped ? `${skipped} match the pattern but have real data — kept.` : ""}`);
        return;
      }
      const preview_list = preview.would_delete.slice(0, 8).map((u) => `• ${u.email}`).join("\n");
      const more = preview.would_delete.length > 8 ? `\n…and ${preview.would_delete.length - 8} more` : "";
      const msg = `Delete ${n} automation/test accounts?\n\nPatterns matched: e2e*, otp_e2e*, qa-*, playwright-*, *@example.com, etc.\n\nPreview:\n${preview_list}${more}\n\n${skipped} matching accounts have real data and will be KEPT.\n\nThis cannot be undone.`;
      if (!window.confirm(msg)) return;
      const { data: result } = await api.post("/admin/users/purge-test-accounts", { confirm: true });
      toast.success(`Purged ${result.deleted} test accounts.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purge failed");
    }
  };
  const resetPw = async () => {
    if (newPw.length < 8) return toast.error("Password >= 8 chars");
    try {
      await api.post(`/admin/users/${userDetail.user.user_id}/reset-password`, { new_password: newPw });
      toast.success("Password reset");
      setResetPwOpen(false); setNewPw("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const createUser = async () => {
    if (!newUser.email || !newUser.password) return toast.error("Email & password required");
    try {
      await api.post("/admin/users", newUser);
      toast.success("User created");
      setCreateOpen(false);
      setNewUser({ email: "", password: "", name: "", role: "hotel_partner", phone: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="bg-slate-50 min-h-[calc(100vh-4rem)]">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="hw-label text-blue-700">Mission Control</div>
            <h1 className="hw-head text-4xl md:text-5xl font-black text-slate-900 mt-3">Admin Panel</h1>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin/support" className="hw-card p-3 px-4 border-emerald-300 bg-emerald-50 flex items-center gap-2 hover:hw-shadow-pop-amber transition" data-testid="admin-support-link">
              <span className="hw-label text-emerald-800">Support tickets →</span>
            </Link>
            {stats?.hotels_pending > 0 && (
              <div className="hw-card p-3 px-4 flex items-center gap-2 border-blue-500 hw-shadow-pop-amber" data-testid="pending-banner">
                <AlertCircle className="w-5 h-5 text-emerald-600" />
                <span className="hw-label text-slate-700">{stats.hotels_pending} hotel{stats.hotels_pending > 1 ? "s" : ""} awaiting your approval</span>
              </div>
            )}
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mt-8" data-testid="admin-stats">
            {[
              ["Hotels", stats.hotels],
              ["Pending", stats.hotels_pending],
              ["Users", stats.users],
              ["Negotiations", stats.negotiations],
              ["Requests", stats.booking_requests],
              ["Bookings", stats.bookings],
            ].map(([l, v]) => (
              <div key={l} className="hw-card p-4">
                <div className="hw-label text-slate-500">{l}</div>
                <div className="hw-head text-3xl font-black text-slate-900 mt-1">{v}</div>
              </div>
            ))}
          </div>
        )}

        {/* Iter35 — Waitlist per vertical (Coming Soon interest) */}
        <div className="mt-6 rounded-2xl border border-[#C8A34D]/40 bg-gradient-to-br from-[#FBF3DA] via-white to-[#F7EAC4] p-6" data-testid="admin-waitlist-tile">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="ss-label text-[#A6852F]">Waitlist · Coming Soon Verticals</div>
              <h3 className="font-head text-xl font-black text-slate-900 mt-1">Traveller interest by vertical</h3>
              <p className="text-xs text-slate-600 mt-1 font-body">Every signup is stored in <code className="text-[10px] bg-white/60 px-1 py-0.5 rounded">waitlist</code> — download the CSV to hand off to Mailchimp / Sendgrid.</p>
            </div>
            <div className="text-right flex flex-col items-end gap-2">
              <div>
                <div className="ss-label text-[#A6852F]">Total</div>
                <div className="font-head text-3xl font-black text-slate-900" data-testid="waitlist-total">
                  {Object.values(waitlist).reduce((a, b) => a + (b || 0), 0)}
                </div>
              </div>
              <a
                href={`${(process.env.REACT_APP_BACKEND_URL || "")}/api/admin/waitlist/export`}
                target="_blank"
                rel="noopener noreferrer"
                className="ss-btn ss-btn-primary text-xs px-3 py-2"
                data-testid="waitlist-export-csv-btn"
              >
                Export CSV
              </a>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-5">
            {[
              ["flights",  "Flights"],
              ["trains",   "Trains"],
              ["buses",    "Buses"],
              ["cabs",     "Cabs"],
              ["holidays", "Holidays"],
            ].map(([k, l]) => (
              <div key={k} className="bg-white/80 border border-[#C8A34D]/30 rounded-xl p-3" data-testid={`waitlist-tile-${k}`}>
                <div className="ss-label text-[#A6852F]">{l}</div>
                <div className="font-head text-2xl font-black text-slate-900 mt-1">{waitlist[k] || 0}</div>
              </div>
            ))}
          </div>
        </div>

        <Tabs defaultValue="approval" className="mt-10">
          <TabsList className="rounded-sm bg-white border border-slate-200 p-1 flex-wrap h-auto">
            <TabsTrigger value="approval" data-testid="tab-approval" className="rounded-sm data-[state=active]:bg-emerald-600 data-[state=active]:text-slate-900 font-bold">Hotel Approval {stats?.hotels_pending > 0 && <span className="ml-1 hw-chip bg-emerald-100 border-blue-500 text-emerald-800 px-1.5 py-0.5">{stats.hotels_pending}</span>}</TabsTrigger>
            <TabsTrigger value="pending-partners" data-testid="tab-pending-partners" className="rounded-sm data-[state=active]:bg-emerald-600 data-[state=active]:text-slate-900 font-bold">Pending Partners {pendingPartners.length > 0 && <span className="ml-1 hw-chip bg-amber-100 border-amber-500 text-amber-800 px-1.5 py-0.5">{pendingPartners.length}</span>}</TabsTrigger>
            <TabsTrigger value="hotels" data-testid="tab-hotels" className="rounded-sm data-[state=active]:bg-emerald-600 data-[state=active]:text-slate-900 font-bold">All Hotels</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users" className="rounded-sm data-[state=active]:bg-emerald-600 data-[state=active]:text-slate-900 font-bold">Users</TabsTrigger>
            <TabsTrigger value="leads" data-testid="tab-leads" className="rounded-sm data-[state=active]:bg-emerald-600 data-[state=active]:text-slate-900 font-bold">Leads</TabsTrigger>
          </TabsList>

          <TabsContent value="approval" className="mt-5 space-y-3">
            {hotels.filter((h) => h.status === "pending").length === 0 ? (
              <div className="hw-card p-10 text-center text-slate-600">No pending approvals.</div>
            ) : hotels.filter((h) => h.status === "pending").map((h) => (
              <div key={h.hotel_id} className="hw-card p-5 flex items-start justify-between gap-4 flex-wrap" data-testid={`pending-${h.hotel_id}`}>
                <div>
                  <h3 className="hw-head font-bold text-lg text-slate-900">{h.name}</h3>
                  <div className="text-sm text-slate-500">{h.city} · GST: {h.gst || "—"} · PAN: {h.pan || "—"}</div>
                  <p className="text-sm text-slate-600 mt-1">{h.description}</p>
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => setHotelDetail(h)} variant="outline" className="rounded-sm" data-testid={`view-pending-${h.hotel_id}`}><Eye className="w-4 h-4 mr-1" /> Review</Button>
                  <Button onClick={() => approve(h.hotel_id)} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-bold" data-testid={`approve-${h.hotel_id}`}>Approve</Button>
                  <Button onClick={() => reject(h.hotel_id)} variant="outline" className="rounded-sm border-red-300 text-red-700" data-testid={`reject-${h.hotel_id}`}>Reject</Button>
                </div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="pending-partners" className="mt-5 space-y-4" data-testid="pending-partners-panel">
            {pendingPartners.length === 0 ? (
              <div className="hw-card p-8 text-center text-slate-500">No pending partners waiting for approval.</div>
            ) : pendingPartners.map((p) => {
              const v = p.validation || {};
              const docs = p.docs || [];
              const requiredDocs = docs.filter((d) => d.kind !== 'owner_id');
              const allDocsUploaded = requiredDocs.every((d) => d.uploaded);
              const allValidationPass = v.gstin_valid && v.pan_valid;
              const canApprove = allDocsUploaded && allValidationPass && (p.photos_count || 0) >= 3 && verifiedPartners[p.user_id];
              return (
              <div key={p.user_id} className="hw-card p-5" data-testid={`pending-${p.user_id}`}>
                <div className="flex flex-wrap items-start gap-4">
                  <div className="flex-1 min-w-[240px]">
                    <div className="hw-head font-black text-lg text-slate-900">{p.name}</div>
                    <div className="text-sm text-slate-500 mt-0.5">{p.phone || "(no phone)"}{p.email ? ` · ${p.email}` : ""}</div>
                    {p.hotel ? (
                      <div className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm">
                        <div className="font-bold text-slate-900">{p.hotel.name || "(no hotel name yet)"}</div>
                        <div className="text-slate-600">{p.hotel.city} · {p.hotel.category || "—"} · Base ₹{p.hotel.base_price || 0}</div>
                        <div className="text-slate-500 text-xs mt-1">{p.hotel.address || "(address pending)"}</div>
                      </div>
                    ) : (
                      <div className="mt-3 ss-label text-amber-700">Partner hasn&apos;t submitted hotel details yet.</div>
                    )}
                  </div>
                </div>

                {/* Iter42 — Compliance review panel */}
                {p.hotel && (
                  <div className="mt-4 rounded-xl border border-[#C8A34D]/30 bg-[#FBF3DA]/40 p-4" data-testid={`review-panel-${p.user_id}`}>
                    <div className="ss-label text-[#A6852F] mb-3">Compliance review</div>

                    {/* GSTIN + PAN + Phone regex checks */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                      <ValidationBox label="GSTIN" value={p.hotel.gst} valid={v.gstin_valid} testId={`gstin-${p.user_id}`} />
                      <ValidationBox label="PAN"   value={p.hotel.pan} valid={v.pan_valid}   testId={`pan-${p.user_id}`} />
                      <ValidationBox label="Phone" value={p.hotel.contact_phone} valid={v.phone_valid} testId={`phone-${p.user_id}`} />
                    </div>

                    {/* Uploaded documents */}
                    <div className="ss-label text-slate-700 mb-2">Uploaded documents</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
                      {docs.map((d) => (
                        <DocLink key={d.kind} doc={d} testId={`doc-${d.kind}-${p.user_id}`} />
                      ))}
                    </div>
                    <div className="text-xs text-slate-600">
                      Photos uploaded: <span className={`font-bold ${(p.photos_count || 0) >= 3 ? "text-emerald-700" : "text-red-700"}`} data-testid={`photos-count-${p.user_id}`}>{p.photos_count || 0}</span> / 3 required
                    </div>

                    {/* Verification checkbox */}
                    <label className="mt-4 flex items-center gap-2 text-sm cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={!!verifiedPartners[p.user_id]}
                        onChange={(e) => setVerifiedPartners((prev) => ({ ...prev, [p.user_id]: e.target.checked }))}
                        className="w-4 h-4 accent-[#C8A34D]"
                        data-testid={`verify-checkbox-${p.user_id}`}
                      />
                      <span className="text-slate-800">
                        I have reviewed all uploaded documents and confirmed the GSTIN &amp; PAN details.
                      </span>
                    </label>

                    {!canApprove && verifiedPartners[p.user_id] && (
                      <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2" data-testid={`cannot-approve-${p.user_id}`}>
                        {!allDocsUploaded && "Missing required documents (GST / PAN / Bank / Trade License). "}
                        {!allValidationPass && "GSTIN or PAN failed format validation. "}
                        {(p.photos_count || 0) < 3 && "Fewer than 3 hotel photos uploaded. "}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex gap-2 mt-4 justify-end">
                  <Button
                    onClick={() => approvePartner(p.user_id)}
                    disabled={!canApprove}
                    className="ss-btn ss-btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid={`approve-partner-${p.user_id}`}
                  >
                    Approve
                  </Button>
                  <Button
                    onClick={() => rejectPartner(p.user_id)}
                    variant="outline"
                    className="rounded-lg border-red-500 text-red-700 hover:bg-red-50"
                    data-testid={`reject-partner-${p.user_id}`}
                  >
                    Request changes
                  </Button>
                </div>
              </div>
              );
            })}
          </TabsContent>

          <TabsContent value="hotels" className="mt-5">
            <div className="hw-card overflow-x-auto" data-testid="hotels-table">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 hw-label text-slate-500">
                  <tr><th className="text-left p-3">Hotel</th><th className="text-left p-3">City</th><th className="text-left p-3">Status</th><th className="text-left p-3">Plan</th><th className="text-left p-3">Actions</th></tr>
                </thead>
                <tbody>
                  {hotels.map((h) => (
                    <tr key={h.hotel_id} className="border-t border-slate-100 even:bg-slate-50/50" data-testid={`row-${h.hotel_id}`}>
                      <td className="p-3 font-semibold text-slate-900">{h.name}</td>
                      <td className="p-3 text-slate-700">{h.city}</td>
                      <td className="p-3"><span className={`hw-chip border ${h.status === "approved" ? "border-emerald-300 bg-emerald-50 text-emerald-700" : h.status === "pending" ? "border-emerald-300 bg-emerald-50 text-blue-700" : "border-red-300 bg-red-50 text-red-700"}`}>{h.status}</span></td>
                      <td className="p-3">
                        <Select value={h.plan} onValueChange={(v) => setPlan(h.hotel_id, v)}>
                          <SelectTrigger className="rounded-sm w-32 h-8" data-testid={`plan-${h.hotel_id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{["free", "silver", "gold", "premium"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                      <td className="p-3 flex gap-1">
                        <Button onClick={() => setHotelDetail(h)} variant="outline" size="sm" className="rounded-sm" data-testid={`view-hotel-${h.hotel_id}`}><Eye className="w-3 h-3" /></Button>
                        <Button onClick={() => delHotel(h.hotel_id)} variant="outline" size="sm" className="rounded-sm border-red-300 text-red-700" data-testid={`del-hotel-${h.hotel_id}`}><Trash2 className="w-3 h-3" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="users" className="mt-5">
            <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
              <div className="hw-label text-slate-500">Total: {users.length} users</div>
              <div className="flex gap-2">
                <Button
                  onClick={purgeTestAccounts}
                  variant="outline"
                  className="rounded-lg border-[#C8A34D] text-[#A6852F] hover:bg-[#FBF3DA]"
                  data-testid="purge-test-users-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Purge test accounts
                </Button>
                <Button onClick={() => setCreateOpen(true)} className="ss-btn ss-btn-primary rounded-lg" data-testid="create-user-btn"><UserPlus className="w-4 h-4 mr-1" /> Create user</Button>
              </div>
            </div>
            <div className="hw-card overflow-x-auto" data-testid="users-table">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 hw-label text-slate-500">
                  <tr><th className="text-left p-3">Name</th><th className="text-left p-3">Email</th><th className="text-left p-3">Role</th><th className="text-left p-3">Auth</th><th className="text-left p-3">Actions</th></tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.user_id} className="border-t border-slate-100 even:bg-slate-50/50" data-testid={`user-row-${u.user_id}`}>
                      <td className="p-3 font-semibold text-slate-900">{u.name}</td>
                      <td className="p-3 text-slate-700">{u.email}</td>
                      <td className="p-3"><span className="hw-chip border-emerald-300 bg-emerald-50 text-blue-700">{(u.role === "customer" ? "traveller" : u.role) || "—"}</span></td>
                      <td className="p-3"><span className="hw-chip border-slate-200 bg-slate-50 text-slate-600">{u.auth_provider || "google"}</span></td>
                      <td className="p-3 flex gap-1">
                        <Button onClick={() => openUser(u.user_id)} variant="outline" size="sm" className="rounded-sm" data-testid={`view-user-${u.user_id}`}><Eye className="w-3 h-3" /></Button>
                        <Button onClick={() => delUser(u.user_id)} variant="outline" size="sm" className="rounded-sm border-red-300 text-red-700" data-testid={`del-user-${u.user_id}`}><Trash2 className="w-3 h-3" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="leads" className="mt-5 grid md:grid-cols-2 gap-5">
            <div className="hw-card p-5">
              <div className="hw-label text-slate-500">Negotiations</div>
              <div className="space-y-2 mt-3">
                {leads.negotiations.slice(0, 15).map((n) => (
                  <div key={n.negotiation_id} className="border-b border-slate-100 pb-2 last:border-0">
                    <div className="font-semibold text-sm text-slate-900">{n.customer_name} → {n.hotel_name}</div>
                    <div className="text-xs text-slate-500">₹{n.offer_price} · {n.status}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="hw-card p-5">
              <div className="hw-label text-slate-500">Booking Requests</div>
              <div className="space-y-2 mt-3">
                {leads.booking_requests.slice(0, 15).map((r) => (
                  <div key={r.request_id} className="border-b border-slate-100 pb-2 last:border-0">
                    <div className="font-semibold text-sm text-slate-900">{r.city} · {r.rooms_needed} rooms</div>
                    <div className="text-xs text-slate-500">{r.event_type} · {r.status}</div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* User Detail Modal */}
      <Dialog open={!!userDetail} onOpenChange={(o) => !o && setUserDetail(null)}>
        <DialogContent className="rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="user-detail-dialog">
          <DialogHeader><DialogTitle className="hw-head font-black text-2xl">{userDetail?.user.name}</DialogTitle></DialogHeader>
          {userDetail && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Info l="Email" v={userDetail.user.email} />
                <Info l="Role" v={(userDetail.user.role === "customer" ? "traveller" : userDetail.user.role) || "—"} />
                <Info l="Phone" v={userDetail.user.phone || "—"} />
                <Info l="Auth provider" v={userDetail.user.auth_provider || "google"} />
              </div>

              <div className="flex flex-wrap gap-2">
                {userDetail.user.auth_provider === "password" && (
                  <Button onClick={() => setResetPwOpen(true)} variant="outline" className="rounded-sm" data-testid="reset-pw-btn"><KeyRound className="w-4 h-4 mr-1" /> Reset password</Button>
                )}
                <Button onClick={() => delUser(userDetail.user.user_id)} variant="outline" className="rounded-sm border-red-300 text-red-700" data-testid="del-user-detail"><Trash2 className="w-4 h-4 mr-1" /> Delete user</Button>
              </div>

              {userDetail.owned_hotels?.length > 0 && (
                <Section title="Owned hotels">
                  {userDetail.owned_hotels.map((h) => (
                    <div key={h.hotel_id} className="text-sm border-b border-slate-100 pb-2 last:border-0 flex items-center justify-between">
                      <div><span className="font-semibold text-slate-900">{h.name}</span> · {h.city} · {h.status}</div>
                      <Button onClick={() => { setHotelDetail(h); }} variant="outline" size="sm" className="rounded-sm"><Eye className="w-3 h-3" /></Button>
                    </div>
                  ))}
                </Section>
              )}

              <Section title={`Bookings as traveller (${userDetail.bookings.length})`}>
                {userDetail.bookings.length === 0 ? <p className="text-sm text-slate-500">None.</p> : userDetail.bookings.slice(0, 10).map((b) => (
                  <div key={b.booking_id} className="text-sm border-b border-slate-100 pb-2 last:border-0">
                    <div><span className="font-semibold text-slate-900">{b.hotel_name}</span> · ₹{b.price} · {b.checkin} → {b.checkout}</div>
                    <div className="text-xs hw-mono text-slate-500">UPI: {b.upi_txn_id || "—"} · {b.status}</div>
                  </div>
                ))}
              </Section>

              {userDetail.partner_bookings?.length > 0 && (
                <Section title={`Reservations at their hotels (${userDetail.partner_bookings.length})`}>
                  {userDetail.partner_bookings.slice(0, 10).map((b) => (
                    <div key={b.booking_id} className="text-sm border-b border-slate-100 pb-2 last:border-0">
                      <div><span className="font-semibold text-slate-900">{b.customer_name}</span> at {b.hotel_name} — ₹{b.price}</div>
                      <div className="text-xs text-slate-500">{b.checkin} → {b.checkout}</div>
                    </div>
                  ))}
                </Section>
              )}

              <Section title={`Negotiations (${userDetail.negotiations.length})`}>
                {userDetail.negotiations.length === 0 ? <p className="text-sm text-slate-500">None.</p> : userDetail.negotiations.slice(0, 10).map((n) => (
                  <div key={n.negotiation_id} className="text-sm border-b border-slate-100 pb-2 last:border-0">
                    <span className="font-semibold text-slate-900">{n.hotel_name}</span> · Offer ₹{n.offer_price} · {n.status}
                  </div>
                ))}
              </Section>
            </div>
          )}
          <DialogFooter><Button onClick={() => setUserDetail(null)} variant="outline" className="rounded-sm">Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPwOpen} onOpenChange={setResetPwOpen}>
        <DialogContent className="rounded-sm" data-testid="reset-pw-dialog">
          <DialogHeader><DialogTitle className="hw-head font-black">Reset password</DialogTitle></DialogHeader>
          <Label className="hw-label">New password</Label>
          <Input data-testid="reset-pw-input" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} className="rounded-sm mt-1" />
          <DialogFooter>
            <Button onClick={() => setResetPwOpen(false)} variant="outline" className="rounded-sm">Cancel</Button>
            <Button onClick={resetPw} className="bg-emerald-600 hover:bg-emerald-700 text-slate-900 rounded-sm font-bold" data-testid="reset-pw-confirm">Reset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hotel Detail Modal */}
      <Dialog open={!!hotelDetail} onOpenChange={(o) => !o && setHotelDetail(null)}>
        <DialogContent className="rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="hotel-detail-dialog">
          <DialogHeader><DialogTitle className="hw-head font-black text-2xl flex items-center gap-2"><HotelIcon className="w-5 h-5 text-emerald-600" /> {hotelDetail?.name}</DialogTitle></DialogHeader>
          {hotelDetail && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Info l="City" v={hotelDetail.city} />
                <Info l="Category" v={hotelDetail.category || "—"} />
                <Info l="Status" v={hotelDetail.status} />
                <Info l="Plan" v={hotelDetail.plan} />
                <Info l="Base price" v={`₹${hotelDetail.base_price?.toLocaleString("en-IN")}`} />
                <Info l="Rating" v={hotelDetail.rating} />
                <Info l="Address" v={hotelDetail.address || "—"} full />
                <Info l="Contact" v={`${hotelDetail.contact_name || "—"} • ${hotelDetail.contact_phone || "—"} • ${hotelDetail.contact_email || "—"}`} full />
                {hotelDetail.map_url && <Info l="Location" v={<a href={hotelDetail.map_url} target="_blank" rel="noreferrer" className="text-blue-700 inline-flex items-center gap-1 hover:underline">Open in Maps <ExternalLink className="w-3 h-3" /></a>} full />}
                <Info l="Near airport" v={hotelDetail.near_airport || "—"} />
                <Info l="Near railway" v={hotelDetail.near_railway || "—"} />
                <Info l="Near bus stand" v={hotelDetail.near_bus_stand || "—"} />
                {hotelDetail.nearby_attractions?.length > 0 && <Info l="Nearby attractions" v={hotelDetail.nearby_attractions.join(" · ")} full />}
              </div>

              <Section title="Verification documents">
                <div className="space-y-2">
                  {REQUIRED_DOCS.map((d) => (
                    <div key={d.key} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2 last:border-0">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-slate-400" />
                        <div>
                          <div className="font-semibold text-slate-900">{d.label}</div>
                          {d.numberKey && hotelDetail[d.numberKey] && <div className="hw-mono text-xs text-slate-500">{hotelDetail[d.numberKey]}</div>}
                        </div>
                      </div>
                      {hotelDetail[d.key] ? (
                        <a
                          href={hotelDetail[d.key].startsWith("/api/uploads/")
                            ? `${process.env.REACT_APP_BACKEND_URL || ""}${hotelDetail[d.key]}`
                            : hotelDetail[d.key]}
                          target="_blank" rel="noreferrer"
                          className="hw-chip border-emerald-300 bg-emerald-50 text-blue-700 hover:bg-emerald-100"
                          data-testid={`doc-${d.key}`}
                        >
                          Open <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : (
                        <span className="hw-chip border-red-200 bg-red-50 text-red-700">Missing</span>
                      )}
                    </div>
                  ))}
                </div>
              </Section>

              <div className="flex flex-wrap gap-2">
                {hotelDetail.status !== "approved" && <Button onClick={() => approve(hotelDetail.hotel_id)} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-bold" data-testid="modal-approve-btn">Approve</Button>}
                {hotelDetail.status !== "rejected" && <Button onClick={() => reject(hotelDetail.hotel_id)} variant="outline" className="rounded-sm border-red-300 text-red-700">Reject</Button>}
                <Button onClick={() => delHotel(hotelDetail.hotel_id)} variant="outline" className="rounded-sm border-red-300 text-red-700"><Trash2 className="w-4 h-4 mr-1" /> Delete</Button>
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={() => setHotelDetail(null)} variant="outline" className="rounded-sm">Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="rounded-sm" data-testid="create-user-dialog">
          <DialogHeader><DialogTitle className="hw-head font-black">Create user</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="hw-label">Name</Label><Input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} className="rounded-sm mt-1" data-testid="cu-name" /></div>
            <div><Label className="hw-label">Email *</Label><Input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} className="rounded-sm mt-1" data-testid="cu-email" /></div>
            <div><Label className="hw-label">Password *</Label><Input type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} className="rounded-sm mt-1" data-testid="cu-password" /></div>
            <div><Label className="hw-label">Phone</Label><Input value={newUser.phone} onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })} className="rounded-sm mt-1" data-testid="cu-phone" /></div>
            <div>
              <Label className="hw-label">Role</Label>
              <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="cu-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hotel_partner">Hotel Partner</SelectItem>
                  <SelectItem value="customer">Traveller</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setCreateOpen(false)} variant="outline" className="rounded-sm">Cancel</Button>
            <Button onClick={createUser} className="bg-emerald-600 hover:bg-emerald-700 text-slate-900 rounded-sm font-bold" data-testid="cu-submit">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ l, v, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="hw-label text-slate-500">{l}</div>
      <div className="text-slate-900 mt-0.5">{v}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border-t border-slate-100 pt-4">
      <div className="hw-label text-blue-700 mb-3">{title}</div>
      {children}
    </div>
  );
}

function ValidationBox({ label, value, valid, testId }) {
  return (
    <div className={`rounded-lg border p-2.5 ${valid ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50"}`} data-testid={testId}>
      <div className="ss-label text-slate-500">{label}</div>
      <div className="font-mono text-sm text-slate-900 truncate">{value || <span className="text-slate-400 italic">not provided</span>}</div>
      <div className={`text-xs font-bold mt-0.5 ${valid ? "text-emerald-700" : "text-red-700"}`}>
        {valid ? "✓ Valid format" : "✗ Invalid format"}
      </div>
    </div>
  );
}

function DocLink({ doc, testId }) {
  // SEC-005 (P3): drop `?auth=<token>` query param. Same-origin `<a href>`
  // navigation carries the HttpOnly session cookie automatically.
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const href = doc.download_url ? `${backend}${doc.download_url}` : null;
  const size = doc.size ? (doc.size < 1024 ? `${doc.size} B` : doc.size < 1024 * 1024 ? `${Math.round(doc.size / 1024)} KB` : `${(doc.size / (1024 * 1024)).toFixed(1)} MB`) : "";
  return (
    <div className={`rounded-lg border p-2.5 flex items-center justify-between gap-2 ${doc.uploaded ? "border-emerald-300 bg-white" : "border-slate-200 bg-slate-50"}`} data-testid={testId}>
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-sm text-slate-900 truncate">{doc.label}</div>
        <div className="text-xs text-slate-500 truncate">
          {doc.uploaded ? <>{doc.original_filename || doc.file_id} · {size}</> : <span className="text-red-700 font-semibold">Not uploaded</span>}
        </div>
      </div>
      {doc.uploaded && href && (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs font-bold text-[#A6852F] underline hover:no-underline shrink-0"
          data-testid={`${testId}-view`}
        >
          View →
        </a>
      )}
    </div>
  );
}

